/**
 * EVChargerDatabase - EV Charger Equipment Database
 *
 * Full CRUD interface for managing EV charger equipment in the database.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert } from '@mui/material';
import { EvStation as EVChargerIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { vortxColors } from '@vortx/shared-ui';
import { useAuth } from '@vortx/shared-ui';
import EquipmentTable from '../EquipmentTable';
import EquipmentForm from './EquipmentForm';
import { API_BASE_URL } from '../../../services/api';

const EV_CHARGER_COLUMNS = [
    { id: 'manufacturer', label: 'Manufacturer', width: '18%' },
    { id: 'model', label: 'Model', width: '18%' },
    { id: 'charger_type', label: 'Charger Type', width: '14%', highlight: true },
    { id: 'power_output_kw', label: 'Power (kW)', width: '12%', highlight: true, render: (v) => v?.toLocaleString() },
    { id: 'equipment_cost', label: 'Equipment ($M)', width: '14%', highlight: true, render: (v) => v ? `$${(v/1000000).toFixed(2)}M` : '-' },
    { id: 'status', label: 'Status', width: '10%' }
];

function EVChargerDatabase() {
    const { currentUser } = useAuth();
    const [equipment, setEquipment] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editingEquipment, setEditingEquipment] = useState(null);

    const fetchEquipment = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/equipment/ev_charger?status=active`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to fetch EV charger equipment');
            const data = await res.json();

            // Sort alphabetically by manufacturer, then by model
            const sorted = (data.equipment || []).sort((a, b) => {
                const manufacturerCompare = (a.manufacturer || '').localeCompare(b.manufacturer || '');
                if (manufacturerCompare !== 0) return manufacturerCompare;
                return (a.model || '').localeCompare(b.model || '');
            });

            setEquipment(sorted);
        } catch (err) {
            console.error('[EVChargerDatabase] Error fetching equipment:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchEquipment();
    }, [fetchEquipment]);

    const handleCreate = () => {
        setEditingEquipment(null);
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditingEquipment(item);
        setShowForm(true);
    };

    const handleCopy = (item) => {
        const { id, created_at, created_by, created_by_email, updated_at, version_history, ...copiedData } = item;
        setEditingEquipment(copiedData);
        setShowForm(true);
    };

    const handleSave = async (data) => {
        try {
            const token = await currentUser.getIdToken();
            const url = editingEquipment?.id
                ? `${API_BASE_URL}/api/site_dna/v1/equipment/ev_charger/${editingEquipment.id}`
                : `${API_BASE_URL}/api/site_dna/v1/equipment/ev_charger`;
            const method = editingEquipment?.id ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(data)
            });

            if (!res.ok) throw new Error('Failed to save equipment');

            setShowForm(false);
            setEditingEquipment(null);
            fetchEquipment();
        } catch (err) {
            console.error('[EVChargerDatabase] Error saving equipment:', err);
            alert(`Error: ${err.message}`);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Delete ${item.manufacturer} ${item.model}?`)) return;

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/equipment/ev_charger/${item.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('Failed to delete equipment');
            fetchEquipment();
        } catch (err) {
            console.error('[EVChargerDatabase] Error deleting equipment:', err);
            alert(`Error: ${err.message}`);
        }
    };

    return (
        <Box>
            <Alert
                severity="info"
                icon={<EVChargerIcon />}
                sx={{
                    mb: 3,
                    bgcolor: alpha(vortxColors.gold, 0.1),
                    color: vortxColors.white,
                    '& .MuiAlert-icon': { color: vortxColors.gold }
                }}
            >
                Manage EV charger specifications and pricing. Supports Level 2 AC and DC Fast Charging.
            </Alert>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <EquipmentTable
                title="EV Chargers"
                description="Manage EV charger specifications and pricing."
                columns={EV_CHARGER_COLUMNS}
                data={equipment}
                loading={loading}
                onAdd={handleCreate}
                onEdit={handleEdit}
                onCopy={handleCopy}
                onDelete={handleDelete}
                onRefresh={fetchEquipment}
                emptyMessage="No EV chargers in database. Click 'Add Entry' to create one."
            />

            {showForm && (
                <EquipmentForm
                    open={showForm}
                    type="ev_charger"
                    equipment={editingEquipment}
                    onSave={handleSave}
                    onCancel={() => {
                        setShowForm(false);
                        setEditingEquipment(null);
                    }}
                />
            )}
        </Box>
    );
}

export default EVChargerDatabase;
